import type { Deliberation } from '@/types/deliberation'

export function isPredeliberationResponsibleForDeliberation(
	deliberation: Deliberation | null | undefined,
	personnelId: number | undefined | null
): boolean {
	if (!deliberation?.predeliberation || personnelId == null) return false
	const respId = deliberation.predeliberation.responsable_id
	if (!respId) return false
	return Number(respId) === Number(personnelId)
}

/** RP session assigné sur la session de cette délibération (principal ou suppléant). */
export function isSessionPedagogicalResponsibleForDeliberation(
	deliberation: Deliberation | null | undefined,
	personnelId: number | undefined | null
): boolean {
	if (!deliberation?.session || personnelId == null) return false
	const session = deliberation.session
	const pid = Number(personnelId)
	const principalId = session.responsable_pedagogique_principal_id
		? Number(session.responsable_pedagogique_principal_id)
		: null
	const suppleantId = session.responsable_pedagogique_suppleant_id
		? Number(session.responsable_pedagogique_suppleant_id)
		: null
	return (
		(principalId !== null && principalId === pid && principalId !== 0) ||
		(suppleantId !== null && suppleantId === pid && suppleantId !== 0)
	)
}

export function canViewDeliberationDocuments(
	deliberation: Deliberation | null | undefined,
	personnelId: number | undefined | null,
	flags: { isAdmin?: boolean; isSuperAdmin?: boolean }
): boolean {
	if (flags.isAdmin || flags.isSuperAdmin) return true
	if (isSessionPedagogicalResponsibleForDeliberation(deliberation, personnelId)) return true
	return isPredeliberationResponsibleForDeliberation(deliberation, personnelId)
}

export function canViewDeliberationRattrapage(
	deliberation: Deliberation | null | undefined,
	personnelId: number | undefined | null,
	flags: { isAdmin?: boolean; isSuperAdmin?: boolean }
): boolean {
	return canViewDeliberationDocuments(deliberation, personnelId, flags)
}

export function canViewPredeliberationLink(
	deliberation: Deliberation | null | undefined,
	personnelId: number | undefined | null,
	flags: { isAdmin?: boolean; isSuperAdmin?: boolean }
): boolean {
	if (!deliberation?.predeliberation_id) return false
	return canViewDeliberationDocuments(deliberation, personnelId, flags)
}

/** Alias explicite pour téléchargement PDF — même règle que documents/rattrapage. */
export function canDownloadDeliberationDocuments(
	deliberation: Deliberation | null | undefined,
	personnelId: number | undefined | null,
	flags: { isAdmin?: boolean; isSuperAdmin?: boolean }
): boolean {
	return canViewDeliberationDocuments(deliberation, personnelId, flags)
}

/** Président de jury (responsable_id) sur au moins une délibération de la session. */
export function isDeliberationResponsableForSession(
	deliberations: Deliberation[],
	personnelId: number | undefined | null
): boolean {
	if (personnelId == null || deliberations.length === 0) return false
	const pid = Number(personnelId)
	return deliberations.some(
		(d) => d.responsable_id != null && Number(d.responsable_id) === pid
	)
}

/** Responsable prédélibération sur au moins une délibération de la session. */
export function isPredeliberationResponsibleForSession(
	deliberations: Deliberation[],
	personnelId: number | undefined | null
): boolean {
	if (personnelId == null || deliberations.length === 0) return false
	return deliberations.some((d) => isPredeliberationResponsibleForDeliberation(d, personnelId))
}

/** Résumé session/spécialité (chips S1 ✓✓) : admin, RP, professeur responsable ou resp. prédélib. */
export function canViewDeliberationSessionSummary(
	session: Deliberation['session'] | null | undefined,
	personnelId: number | undefined | null,
	flags: { isAdmin?: boolean; isSuperAdmin?: boolean; isPedagogicalManager?: boolean },
	deliberations: Deliberation[] = []
): boolean {
	if (flags.isAdmin || flags.isSuperAdmin || flags.isPedagogicalManager) return true
	if (isSessionPedagogicalResponsibleForDeliberation({ session } as Deliberation, personnelId)) return true
	if (isDeliberationResponsableForSession(deliberations, personnelId)) return true
	if (isPredeliberationResponsibleForSession(deliberations, personnelId)) return true
	return false
}
